/*
  SubroutineSection.jsx
*/

// Npm.
import React, { useState, useCallback } from 'react';

// Components.
import Button from '../../../Button';
import BottomActions from '../BottomActions';
import SubroutineItemForm from './SubroutineItemForm';

// Utils.
import getRandomId from '../../../../utils/getRandomId';

// Makes.
import makeStyles from '../../../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  noneText: {
    color: theme.color_text_disabled,
    textAlign: 'center',
    fontStyle: 'italic',
    margin: theme.space(4, 0),
  },
  subroutineItem: {
    '&.expanded': {
      marginBottom: theme.space(4),
    },
  },
}));

/**
 * Helper to create a new subroutine.
 */
function createSubroutine() {
  return {
    _id: getRandomId(),
    method: '',
    batch: false,
  };
}

/**
 * Helper to ensure "graphData._botech.subroutines" array exists.
 * Returns cloned objects for immutability (excluding "subroutines").
 */
function ensureSubroutines(graphData) {
  const next = { ...graphData };
  next._botech = { ...next._botech };
  next._botech.subroutines = next._botech.subroutines || [];
  return next;
}

/**
 * Section for editing "graphData._botech.subroutines" data.
 */
export default function SubroutineSection({
  className,
  graphData,
  onChangeGraphData,
  ...rest
}) {

  const classes = useStyles();

  const subroutines = graphData._botech?.subroutines || [];

  const [expandedId, setExpandedId] = useState(null);

  // Handlers.

  const handleToggleExpand = useCallback((id) => {
    setExpandedId(id);
  }, [setExpandedId]);

  const handleMoveUp = useCallback((curr) => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        const index = data._botech.subroutines.indexOf(curr);
        if (index > 0) {
          data._botech.subroutines = data._botech.subroutines.concat();
          data._botech.subroutines[index] = data._botech.subroutines[index - 1];
          data._botech.subroutines[index - 1] = curr;
        }
        return data;
      });
    }
  }, [onChangeGraphData]);

  const handleMoveDown = useCallback((curr) => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        const index = data._botech.subroutines.indexOf(curr);
        if (index > -1 && index < data._botech.subroutines.length - 1) {
          data._botech.subroutines = data._botech.subroutines.concat();
          data._botech.subroutines[index] = data._botech.subroutines[index + 1];
          data._botech.subroutines[index + 1] = curr;
        }
        return data;
      });
    }
  }, [onChangeGraphData]);

  const handleChangeSubroutine = useCallback((curr, next) => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        data._botech.subroutines = data._botech.subroutines.map(obj => obj === curr ? next : obj);
        return data;
      });
    }
  }, [onChangeGraphData]);

  const handleAddBelowSubroutine = useCallback((curr) => {
    if (onChangeGraphData) {
      const newSub = createSubroutine();
      setExpandedId(newSub._id);
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        const index = data._botech.subroutines.indexOf(curr);
        if (index > -1) {
          const left = data._botech.subroutines.slice(0, index + 1);
          const right = data._botech.subroutines.slice(index + 1);
          data._botech.subroutines = left.concat(newSub, right);
        }
        return data;
      });
    }
  }, [onChangeGraphData]);

  const handleRemoveSubroutine = useCallback((curr) => {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        data._botech.subroutines = data._botech.subroutines.filter(obj => obj !== curr);
        return data;
      });
    }
  }, [onChangeGraphData]);

  function handleClickAdd() {
    if (onChangeGraphData) {
      onChangeGraphData(data => {
        data = ensureSubroutines(data);
        data._botech.subroutines = data._botech.subroutines.concat(createSubroutine());
        return data;
      });
    }
  }

  return (
    <div className={className}>

      {
        subroutines.length === 0 &&
        <div className={`${classes.noneText} body2`}>
          Currently no Subroutines.
        </div>
      }

      {
        // TODO: Use better "key" prop.
        subroutines.map((subroutine, i) => {

          const expanded = Boolean(expandedId) && expandedId === subroutine._id;
          let useClass = `${classes.subroutineItem} fadeIn`;
          if (expanded) {
            useClass += ' expanded';
          }

          return (
            <SubroutineItemForm
              key={subroutine._id}
              index={i}
              className={useClass}
              subroutine={subroutine}
              nodes={graphData.nodes}
              links={graphData.links}
              expanded={Boolean(expandedId) && expandedId === subroutine._id}
              onToggleExpand={handleToggleExpand}
              onMoveUp={i > 0 ? handleMoveUp : undefined}
              onMoveDown={i < subroutines.length - 1 ? handleMoveDown : undefined}
              onChange={handleChangeSubroutine}
              onAddBelow={handleAddBelowSubroutine}
              onRemove={handleRemoveSubroutine}
            />
          )
        })
      }

      <BottomActions>
        <Button onClick={handleClickAdd}>
          + Add Subroutine
        </Button>
      </BottomActions>

    </div>
  );
}
